import * as Schema from "effect/Schema";
import { ModelReadiness } from "./modelTiers.ts";

export const EffectiveModelAccess = Schema.Struct({
  effective: Schema.Boolean,
  governingScope: Schema.Literals(["actor", "group", "world", "default"]),
  override: Schema.NullOr(Schema.Boolean),
  revision: Schema.Int,
  worldDefault: Schema.NullOr(Schema.Boolean),
  worldRevision: Schema.Int,
  groups: Schema.Array(
    Schema.Struct({
      groupKey: Schema.String,
      name: Schema.String,
      enabled: Schema.NullOr(Schema.Boolean),
      revision: Schema.Int,
    }),
  ),
});
export type EffectiveModelAccess = typeof EffectiveModelAccess.Type;

export const ModelAccessRead = Schema.Struct({
  targetWorldKey: Schema.String,
  provider: Schema.String,
  model: Schema.String,
  subjectActorId: Schema.optionalKey(Schema.String),
  cursor: Schema.optionalKey(Schema.NullOr(Schema.String)),
  limit: Schema.optionalKey(Schema.Int),
});
export type ModelAccessRead = typeof ModelAccessRead.Type;

export const SetModelAccess = Schema.Struct({
  targetWorldKey: Schema.String,
  provider: Schema.String,
  model: Schema.String,
  scope: Schema.Literals(["world", "actor", "group"]),
  subjectActorId: Schema.optionalKey(Schema.String),
  groupKey: Schema.optionalKey(Schema.String),
  enabled: Schema.NullOr(Schema.Boolean),
  requestKey: Schema.String,
  expectedRevision: Schema.Int,
});
export type SetModelAccess = typeof SetModelAccess.Type;

export const ModelAccessView = Schema.Struct({
  schemaVersion: Schema.Literal("model-access-v1"),
  operatorWorldKey: Schema.String,
  world: Schema.Struct({ worldKey: Schema.String, name: Schema.String }),
  model: Schema.Struct({
    provider: Schema.String,
    model: Schema.String,
    offerEnabled: Schema.Boolean,
    offerRevision: Schema.Int,
  }),
  worldPolicy: Schema.Struct({ enabled: Schema.NullOr(Schema.Boolean), revision: Schema.Int }),
  members: Schema.Array(
    Schema.Struct({
      actorId: Schema.String,
      actorKey: Schema.String,
      displayName: Schema.String,
      access: EffectiveModelAccess,
      readiness: Schema.optionalKey(ModelReadiness),
    }),
  ),
  page: Schema.Struct({ cursor: Schema.NullOr(Schema.String), isDone: Schema.Boolean }),
});
export type ModelAccessView = typeof ModelAccessView.Type;

const Policy = Schema.Struct({ enabled: Schema.NullOr(Schema.Boolean), revision: Schema.Int });
const Group = Schema.Struct({
  groupKey: Schema.String,
  name: Schema.String,
  status: Schema.Literals(["active", "retired"]),
  revision: Schema.Int,
  modelPolicy: Schema.optionalKey(Policy),
  categories: Schema.optionalKey(Schema.Record(Schema.String, Policy)),
  /** A group-effect read: the group's policy for each sold model it names (Can 68 step 2, the add-member card). */
  modelPolicies: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        provider: Schema.String,
        model: Schema.String,
        enabled: Schema.NullOr(Schema.Boolean),
        revision: Schema.Int,
      }),
    ),
  ),
  /** A group-effect read: the digest of exactly that access, passed back by the add-member card (W-290eaf36). */
  effectDigest: Schema.optionalKey(Schema.String),
});
export type ModelAccessGroup = typeof Group.Type;
const GroupWrite = {
  targetWorldKey: Schema.String,
  groupKey: Schema.String,
  expectedRevision: Schema.Int,
  requestKey: Schema.String,
};
export const SetModelAccessGroup = Schema.Struct({
  ...GroupWrite,
  name: Schema.String,
  status: Schema.Literals(["active", "retired"]),
});
export type SetModelAccessGroup = typeof SetModelAccessGroup.Type;
export const SetModelAccessGroupMember = Schema.Struct({
  ...GroupWrite,
  subjectActorId: Schema.String,
  active: Schema.Boolean,
  /** An addition: the effectDigest its card showed; the World refuses it if the group gives anything else now. */
  expectedGroupEffect: Schema.optionalKey(Schema.String),
});
export type SetModelAccessGroupMember = typeof SetModelAccessGroupMember.Type;
export const SetModelGroupCategory = Schema.Struct({
  ...GroupWrite,
  kind: Schema.String,
  enabled: Schema.NullOr(Schema.Boolean),
});
export type SetModelGroupCategory = typeof SetModelGroupCategory.Type;
export const ModelGroupRead = Schema.Struct({
  targetWorldKey: Schema.String,
  view: Schema.Literals(["groups", "group-members", "group-effect"]),
  provider: Schema.optionalKey(Schema.String),
  model: Schema.optionalKey(Schema.String),
  groupKey: Schema.optionalKey(Schema.String),
  subjectActorId: Schema.optionalKey(Schema.String),
  cursor: Schema.optionalKey(Schema.NullOr(Schema.String)),
  limit: Schema.optionalKey(Schema.Int),
});
export type ModelGroupRead = typeof ModelGroupRead.Type;
const GroupViewBase = {
  operatorWorldKey: Schema.String,
  world: Schema.Struct({ worldKey: Schema.String, name: Schema.String }),
  page: Schema.Struct({ cursor: Schema.NullOr(Schema.String), isDone: Schema.Boolean }),
};
export const ModelGroupsView = Schema.Struct({
  ...GroupViewBase,
  schemaVersion: Schema.Literal("model-access-groups-v1"),
  groups: Schema.Array(Group),
});
export type ModelGroupsView = typeof ModelGroupsView.Type;
export const ModelGroupMembersView = Schema.Struct({
  ...GroupViewBase,
  schemaVersion: Schema.Literal("model-access-group-members-v1"),
  group: Group,
  members: Schema.Array(
    Schema.Struct({
      actorId: Schema.String,
      actorKey: Schema.NullOr(Schema.String),
      displayName: Schema.String,
      worldMemberActive: Schema.Boolean,
      membership: Schema.Struct({ active: Schema.Boolean, revision: Schema.Int }),
    }),
  ),
});
export type ModelGroupMembersView = typeof ModelGroupMembersView.Type;
